import { useApp } from '@/context/AppContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Mail, Shield, User } from 'lucide-react';

const roleLabels = {
  'admin_igreja': 'Admin Igreja',
  'membro': 'Membro',
};

export const MembrosPage = () => {
  const { membros, loadingMembros, igrejaAtual } = useApp();

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-blue-500/10 via-background to-purple-500/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="secondary" className="text-xs">
              {igrejaAtual?.nome || 'Igreja'}
            </Badge>
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
            Membros
          </h1>
          <p className="text-muted-foreground mt-1">
            {loadingMembros ? 'Carregando...' : `${membros.length} membros cadastrados na igreja`}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          {loadingMembros ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="card-elevated">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="flex-1">
                        <Skeleton className="h-5 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : membros.length === 0 ? (
            <Card className="card-elevated">
              <CardContent className="p-12 text-center">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-xl mb-2">Nenhum membro encontrado</h3>
                <p className="text-muted-foreground">
                  {igrejaAtual 
                    ? 'Ainda não há membros cadastrados nesta igreja.'
                    : 'Selecione ou crie uma igreja primeiro.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {membros.map((membro, index) => (
                <Card 
                  key={membro.id} 
                  className="card-elevated animate-scale-in"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-primary/10 text-primary text-lg">
                          {membro.user_nome?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground truncate">
                            {membro.user_nome || 'Sem nome'}
                          </h3>
                          {membro.role === 'admin_igreja' && (
                            <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {membro.user_email}
                        </p>
                        <Badge 
                          variant={membro.role === 'admin_igreja' ? 'default' : 'secondary'} 
                          className="mt-2"
                        >
                          {roleLabels[membro.role] || membro.role}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
